import React, { useCallback, useMemo, useRef, useState } from "react";
import DocumentAddedIcon from "../../../../../assets/svg/document-added-icon.tsx";
import { getFileTypeIcon } from "../../../../../utils/functions/file-icon-extractor.ts";
import AddIcon from "../../../../../assets/svg/add-icon.tsx";
import Button from "../../../../../components/Button/Button.tsx";
import CloseIcon from "../../../../../assets/svg/close-icon.tsx";
import SearchableDropdown from "../../../../../components/SearchableDropdown/SearchableDropdown.tsx";
import CampaignBranchesDropdown, {
  UseCaseOption,
} from "../../../../../components/CampaignBranchesDropdown/CampaignBranchesDropdown.tsx";
import { customToast } from "../../../../../utils/custom-toast/custom-toast.tsx";
import CompletedIcon from "../../../../../assets/svg/completed-icon.tsx";
import {
  useCheckIfFilesExistMutation,
  useBulkUploadDocumentsMutation,
  useIngestBulkDocumentsMutation,
  useGetUseCasesWithBranchesQuery,
} from "../../../../../redux/services/playgroundApi.ts";
import { Tooltip } from "../../../../../components/Tooltip/Tooltip.tsx";
import CheckIcon from "../../../../../assets/svg/check-icon.tsx";
import AlertIcon from "../../../../../assets/svg/alert-icon.tsx";
import { getTransformPropertiesOptions } from "../../../utils/constants.tsx";
import ClipLoader from "../../../../../components/Common/ClipLoader.tsx";
import { trackEvents } from "../../../../../utils/amplitude/amplitude";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { getRegionLabel } from "../../../../../utils/constants/mappings";

const UploadDocuments = ({
  properties,
  onClose,
  setScrollToSection,
}: {
  properties: { res: { name: string; id: string }[] };
  onClose: () => void;
  setScrollToSection?: (scrollToSection: string | null) => void;
}) => {
  const orgId = useSelector((state: RootState) => state?.auth?.client?.client_code) || "unknown";
  const userId = useSelector((state: RootState) => state?.auth?.user?._id) || "unknown";
  const clientCode = useSelector(
    (state: RootState) => state?.auth?.client?.client_code
  );
  const knowledgeBaseLabel = getRegionLabel("knowledgeBaseSection", clientCode);
  const [file, setFile] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [fileProperties, setFileProperties] = useState<
    Map<string, { id: string; name: string }>
  >(new Map());
  const [fileCampaignBranches, setFileCampaignBranches] = useState<
    Map<string, { useCases: string[]; branches: Map<string, string[]> }>
  >(new Map());
  const [existingFiles, setExistingFiles] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const shouldEnableListScroll = file.length > 5;
  const listContainerClassName = `flex flex-col gap-3 slim-scrollbar ${
    shouldEnableListScroll
      ? "overflow-y-auto max-h-[280px]"
      : "overflow-y-visible"
  }`;
  const maxSize = "225mb";
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [checkIfFilesExist] = useCheckIfFilesExistMutation();
  const [bulkUploadDocuments] = useBulkUploadDocumentsMutation();
  const [ingestBulkDocuments] = useIngestBulkDocumentsMutation();
  const { data: useCasesWithBranchesData } = useGetUseCasesWithBranchesQuery();

  // Transform API response to CampaignBranchesDropdown format
  const useCaseOptions: UseCaseOption[] = useMemo(() => {
    if (!useCasesWithBranchesData?.data) return [];
    return useCasesWithBranchesData.data.map((item) => ({
      id: item.use_case.key,
      name: item.use_case.display_name,
      branches: item.branches.map((branch) => ({
        id: branch.key,
        name: branch.display_name,
      })),
    }));
  }, [useCasesWithBranchesData]);

  const handleFiles = useCallback(
    async (newFiles: FileList | File[]) => {
      const validateFile = (file: File): boolean => {
        const allowedTypes = [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ];

        // Convert maxSize from string (e.g., "2mb") to bytes
        const sizeUnit = maxSize.slice(-2).toLowerCase();
        const sizeValue = parseInt(maxSize.slice(0, -2));
        const maxBytes =
          sizeUnit === "mb" ? sizeValue * 1024 * 1024 : sizeValue * 1024;

        if (!allowedTypes.includes(file.type)) {
          customToast(
            "File type not supported. Please upload DOCX, PDF, or XLSX files.",
            "error",
            undefined,
            undefined,
            "bottom-left",
          );
          return false;
        }

        if (file.size > maxBytes) {
          customToast(
            `File size exceeds ${maxSize} limit.`,
            "error",
            undefined,
            undefined,
            "bottom-left",
          );
          return false;
        }

        return true;
      };

      setFile((prevFiles) => {
        const existingFileNames = new Set(prevFiles.map((f) => f.name));
        const validNewFiles = Array.from(newFiles).filter(
          (f) => validateFile(f) && !existingFileNames.has(f.name),
        );

        if (validNewFiles.length > 0) {
          const remainingSlots = 25 - prevFiles.length;
          const filesToAdd = validNewFiles.slice(0, remainingSlots);
          const fileNames = filesToAdd.map((f) => f.name);
          checkIfFilesExist(fileNames)
            .unwrap()
            .then((response) => {
              const existingFileNames: string[] = [];

              filesToAdd.forEach((file) => {
                if (response[file.name] === true) {
                  existingFileNames.push(file.name);
                }
              });

              // Add all files to UI (both existing and non-existing)
              setFile((latestFiles) => {
                const newFiles = [...latestFiles, ...filesToAdd];
                return newFiles;
              });

              // Track which files already exist
              if (existingFileNames.length > 0) {
                setExistingFiles((prev) => {
                  const updated = new Set(prev);
                  existingFileNames.forEach((fileName) =>
                    updated.add(fileName),
                  );
                  return updated;
                });
              }

              // Initialize property selection for all files with default "-1" (Generic)
              setFileProperties((prev) => {
                const updated = new Map(prev);
                filesToAdd.forEach((f) => {
                  if (!updated.has(f.name)) {
                    updated.set(f.name, { id: "-1", name: "Generic" });
                  }
                });
                return updated;
              });

              // Initialize use case / branch selection for all files (empty by default)
              setFileCampaignBranches((prev) => {
                const updated = new Map(prev);
                filesToAdd.forEach((f) => {
                  if (!updated.has(f.name)) {
                    updated.set(f.name, { useCases: [], branches: new Map() });
                  }
                });
                return updated;
              });
            })
            .catch((error) => {
              console.error("Error checking file existence:", error);
              customToast(
                "Error checking file existence. Please try again.",
                "error",
              );
            });
        }

        return prevFiles;
      });
    },
    [maxSize, checkIfFilesExist],
  );

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === dropZoneRef.current) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const droppedFiles = e.dataTransfer.files;
      handleFiles(droppedFiles);
    },
    [handleFiles],
  );

  const handleClick = () => {
    if (success) {
      setSuccess(false);
      setError(false);
      setIsLoading(false);
      setFile([]);
      setFileProperties(new Map());
      setFileCampaignBranches(new Map());
      setExistingFiles(new Set());
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };
  const bulkUpload = useCallback(
    async (
      files: File[],
      propertiesMap: Map<string, { id: string; name: string }>,
      campaignBranchesMap: Map<
        string,
        { useCases: string[]; branches: Map<string, string[]> }
      >,
    ) => {
      try {
        const response = await bulkUploadDocuments({
          files,
        }).unwrap();

        if (response && Array.isArray(response) && response?.length > 0) {
          try {
            const documentsMetadata = response?.map((file) => {
              const propertyInfo = propertiesMap.get(file?.file_name) || {
                id: "-1",
                name: "Generic",
              };

              // Build campaign_branches array from selected useCases and branches
              const campaignInfo = campaignBranchesMap.get(file?.file_name);
              const campaignBranches: string[] = [];
              if (campaignInfo) {
                // Add all selected use cases
                campaignBranches.push(...campaignInfo.useCases);
                // Add all selected branches
                campaignInfo.branches.forEach((branchIds) => {
                  campaignBranches.push(...branchIds);
                });
              }

              return {
                namespace_id: propertyInfo.id,
                namespace_name: propertyInfo.name,
                file_name: file?.file_name,
                s3_uri: file?.s3_uri,
                campaign_branches: campaignBranches,
              };
            });

            await ingestBulkDocuments(documentsMetadata).unwrap();
            files.forEach((fileItem) => {
              const propertyInfo = propertiesMap.get(fileItem.name) || {
                id: "-1",
                name: "Generic",
              };
              trackEvents("document_uploaded", {
                file_name: fileItem.name,
                file_size: fileItem.size,
                property_name: propertyInfo.name,
                status: "uploaded",
                uploaded_by: userId,
                org_id: orgId,
              });
            });
          } catch (secondApiError) {
            console.error("Error in second API call:", secondApiError);
            setError(true);
            throw secondApiError;
          }
        }

        if (response && Array.isArray(response) && response?.length > 0) {
          //   customToast(
          //     PLAYGROUND_CONFIG.MESSAGES.SUCCESS.UPLOAD(response?.length),
          //     "success"
          //   );
        } else {
          //   customToast(PLAYGROUND_CONFIG.MESSAGES.ERROR.UPLOAD_FAILED, "error");
        }
        return true;
      } catch (error) {
        console.error("Bulk upload error:", error);
        return false;
      }
    },
    [bulkUploadDocuments, ingestBulkDocuments],
  );

  const handleBulkUpload = useCallback(
    async (files: File[]) => {
      setIsLoading(true);
      try {
        // Filter out files that already exist
        const filesToUpload = files.filter(
          (file) => !existingFiles.has(file.name),
        );

        if (filesToUpload.length === 0) {
          customToast(`All files already exist in the ${knowledgeBaseLabel}`, "error");
          setIsLoading(false);
          return false;
        }

        const result = await bulkUpload(
          filesToUpload,
          fileProperties,
          fileCampaignBranches,
        );
        if (result) {
          setSuccess(true);
        }
        return result;
      } catch (error) {
        console.error("Bulk upload error:", error);
        return Promise.reject(error);
      } finally {
        setIsLoading(false);
        setScrollToSection?.("documents");
        onClose();
      }
    },
    [bulkUpload, fileProperties, fileCampaignBranches, existingFiles, knowledgeBaseLabel],
  );

  const handlePropertyChange = useCallback(
    (
      fileName: string,
      value: string,
      option: { label?: string; value: string },
    ) => {
      setFileProperties((prev) => {
        const updated = new Map(prev);
        const propertyName =
          option.label ||
          (value === "-1"
            ? "Generic"
            : properties?.res?.find((p) => p.id === value)?.name || "Generic");
        updated.set(fileName, { id: value, name: propertyName });
        return updated;
      });
    },
    [properties],
  );

  const handleCampaignBranchChange = useCallback(
    (fileName: string, useCases: string[], branches: Map<string, string[]>) => {
      setFileCampaignBranches((prev) => {
        const updated = new Map(prev);
        updated.set(fileName, { useCases, branches });
        return updated;
      });
    },
    [],
  );

  const transformPropertiesOptions = getTransformPropertiesOptions(properties);
  const existingFilesCount = file.filter((f) =>
    existingFiles.has(f.name),
  ).length;

  const existingFilesCountMessage = `${existingFilesCount} of ${file.length} files won't be uploaded`;

  const tableHeader = [
    {
      label: "Document",
      key: "document",
    },
    {
      label: "Type",
      key: "propertyType",
    },
    {
      label: "Use Case / Branch",
      key: "useCaseBranch",
    },
    {
      label: "File Size",
      key: "fileSize",
    },
  ];

  return (
    <div className="flex flex-col gap-2.5 h-full overflow-y-auto slim-scrollbar">
      <DocumentAddedIcon className="w-11.5 h-11.5 text-verba-blue dark:text-verba-blue-dk flex-shrink-0" />
      <h2 className="text-2xl font-medium text-verba-indigo dark:text-verba-indigo-dk">
        Upload Documents
      </h2>
      <p className="text-base text-verba-indigo dark:text-verba-indigo-dk">
        Upload your files and let VerbaFlo AI create fresh, insightful content
        instantly. Documents in{" "}
        <span className="font-semibold">.doc, .pdf or .xlsx</span> formats are
        supported
      </p>
      <div className="flex items-center gap-2">
        <img src={getFileTypeIcon("Doc")} alt="doc" className="w-6 h-6" />
        <img src={getFileTypeIcon("PDF")} alt="pdf" className="w-6 h-6" />
        <img src={getFileTypeIcon("xlsx")} alt="xlsx" className="w-6 h-6" />
      </div>

      <div
        ref={dropZoneRef}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border group hover:border-verba-blue-150 dark:hover:border-verba-blue-150-dk hover:border-dashed border-border-gray dark:border-border-gray-dk hover:bg-verba-blue-light-card-bg dark:hover:bg-verba-blue-light-card-bg-dk rounded-lg py-[26px] px-[18px] cursor-pointer mt-[14px] ${
          isDragging
            ? "border-verba-blue-150 dark:border-verba-blue-150-dk border-dashed bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk"
            : ""
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileInputChange}
          accept=".pdf,.doc,.docx,.xls,.xlsx"
          multiple={true}
          disabled={file.length >= 25}
        />
        <div className="flex items-center gap-2">
          <AddIcon className="w-6 h-6 text-verba-indigo dark:text-verba-indigo-dk group-hover:text-verba-blue dark:group-hover:text-verba-blue-dk" />
          <div className="flex flex-col  items-start">
            <div className="text-base text-verba-indigo dark:text-verba-indigo-dk font-medium group-hover:text-verba-blue dark:group-hover:text-verba-blue-dk">
              Upload Document
            </div>
            <p className="text-sm text-dark-gray dark:text-dark-gray-dk flex gap-1">
              Select or Drag & Drop{" "}
              <span className="font-semibold">DOC, PDF,</span> and{" "}
              <span className="font-semibold">XLSX</span>
              files
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 mt-[14px]">
        <span className="text-base text-dark-gray dark:text-dark-gray-dk font-medium">
          Maximum size:
        </span>
        <span className="text-base text-heading-text dark:text-heading-text-dk font-medium">
          225MB
        </span>
        <div className="w-[1px] h-[17px] bg-border-gray dark:bg-border-gray-dk mx-1"></div>
        <span className="text-base text-dark-gray dark:text-dark-gray-dk font-medium">
          Maximum number of files:
        </span>
        <span className="text-base text-heading-text dark:text-heading-text-dk font-medium">
          25
        </span>
      </div>

      {file && file.length > 0 && (
        <div className="flex flex-col gap-3 p-4 border border-dashed border-verba-blue-150 dark:border-verba-blue-150-dk bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk rounded-lg mb-6 max-h-[400px] mt-[12px]">
          <div className="flex items-center justify-between gap-2 border-b border-border-gray dark:border-border-gray-dk pb-2">
            <p className="text-base font-medium text-verba-indigo dark:text-verba-indigo-dk">
              Selected {file.length} Documents
            </p>
          </div>
          <div className="grid grid-cols-[199px_110px_110px_auto_auto] gap-2 items-center">
            {tableHeader.map((header) => (
              <span
                key={header.key}
                className="text-sm font-semibold text-gray-placeholder dark:text-gray-placeholder-dk"
              >
                {header.label}
              </span>
            ))}
          </div>
          <div className={listContainerClassName}>
            {file.map((fileItem: File) => {
              const isExisting = existingFiles.has(fileItem.name);
              return (
                <div
                  key={fileItem.name}
                  className="grid grid-cols-[199px_110px_110px_auto_auto] gap-2 items-center"
                >
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <img
                        src={getFileTypeIcon(fileItem.name)}
                        alt="doc"
                        className="w-6 h-6 flex-shrink-0"
                      />
                      <Tooltip content={fileItem.name}>
                        <span className="text-sm text-verba-indigo dark:text-verba-indigo-dk font-medium truncate min-w-0">
                          {fileItem.name}
                        </span>
                      </Tooltip>
                    </div>
                  </div>

                  {!isExisting ? (
                    <>
                      <SearchableDropdown
                        options={transformPropertiesOptions}
                        value={fileProperties.get(fileItem.name)?.id || "-1"}
                        onChange={(value, option) =>
                          handlePropertyChange(fileItem.name, value, option)
                        }
                        className="w-[110px]"
                        selectedOptionContainerClassName="w-full"
                        dropdownClassName="!min-w-[280px] max-h-[175px] overflow-y-auto no-scrollbar"
                        dropdownMenuClassName="overflow-y-auto no-scrollbar"
                        sectionHeading="Properties"
                        searchPlaceholder="Search"
                        disabled={success || error}
                      />
                      <CampaignBranchesDropdown
                        useCases={useCaseOptions}
                        selectedUseCases={
                          fileCampaignBranches.get(fileItem.name)?.useCases ||
                          []
                        }
                        selectedBranches={
                          fileCampaignBranches.get(fileItem.name)?.branches ||
                          new Map()
                        }
                        onSelectionChange={(useCases, branches) =>
                          handleCampaignBranchChange(
                            fileItem.name,
                            useCases,
                            branches,
                          )
                        }
                        className="w-[110px]"
                        triggerClassName="w-full"
                        disabled={success || error || isLoading}
                      />
                      <div className="text-xs text-gray-placeholder dark:text-gray-placeholder-dk">
                        {(fileItem.size / (1024 * 1024)).toFixed(2)} MB
                      </div>
                    </>
                  ) : (
                    <div className="col-span-3 flex items-center justify-end mr-[-35px]">
                      <div className="flex items-center gap-1 text-xs font-medium border border-card-yellow-stroke dark:border-card-yellow-stroke-dk text-nowrap bg-card-yellow-bg dark:bg-card-yellow-bg-dk rounded-lg px-2 py-1">
                        <AlertIcon className="w-[14px] h-[14px] text-rating-yellow dark:text-rating-yellow-dk" />
                        <span className="text-verba-indigo dark:text-verba-indigo-dk">
                          File already exists
                        </span>
                      </div>
                    </div>
                  )}
                  {!isLoading && !success && (
                    <button
                      className={`w-[14px] h-[14px]  bg-verba-indigo dark:bg-verba-indigo-dk rounded-full flex flex-col items-center justify-center transition-transform duration-200 hover:scale-110 ${
                        isExisting ? "ml-[38px]" : ""
                      }`}
                      onClick={() => {
                        setFile(file.filter((f) => f.name !== fileItem.name));
                        setFileProperties((prev) => {
                          const updated = new Map(prev);
                          updated.delete(fileItem.name);
                          return updated;
                        });
                        setFileCampaignBranches((prev) => {
                          const updated = new Map(prev);
                          updated.delete(fileItem.name);
                          return updated;
                        });
                        setExistingFiles((prev) => {
                          const updated = new Set(prev);
                          updated.delete(fileItem.name);
                          return updated;
                        });
                      }}
                    >
                      <CloseIcon className="w-[5.5px] h-[5.5px] text-pure-white dark:text-pure-white-dk" />
                    </button>
                  )}
                  {isLoading && !isExisting && (
                    <ClipLoader
                      className="w-[4.5px] h-[4.5px] text-verba-blue dark:text-verba-blue-dk"
                      thickness={2}
                      size={14}
                    />
                  )}
                  {success && !isExisting && (
                    <div className="flex items-center justify-center">
                      <CompletedIcon className="w-4 h-4 text-verba-green-success dark:text-verba-green-success-dk" />{" "}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {isLoading && (
            <div className="flex items-center gap-2 mt-3">
              <ClipLoader
                className="w-[4.5px] h-[4.5px] text-verba-blue dark:text-verba-blue-dk"
                thickness={2}
                size={14}
              />
              <span className="text-xs text-verba-blue dark:text-verba-blue-dk">
                Uploading{" "}
                {file.filter((f) => !existingFiles.has(f.name)).length}{" "}
                documents
              </span>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-1 mt-3">
              <CompletedIcon className="w-4 h-4 text-verba-green-success dark:text-verba-green-success-dk" />{" "}
              <span className="text-xs text-verba-green-success dark:text-verba-green-success-dk font-medium">
                Uploaded
              </span>
            </div>
          )}
          {error && (
            <div className="flex items-center gap-1 mt-3">
              <AlertIcon className="w-[14px] h-[14px] text-verba-red-alert dark:text-verba-red-alert-dk" />
              <span className="text-xs text-verba-red-alert dark:text-verba-red-alert-dk font-medium">
                Failed to upload documents
              </span>
            </div>
          )}
          {!isLoading && !success && !error && (
            <div className="flex gap-2 justify-between items-center mt-3">
              <div className="flex items-center gap-1">
                {existingFilesCount > 0 && (
                  <>
                    <AlertIcon className="w-[18px] h-[18px] text-rating-yellow dark:text-rating-yellow-dk" />
                    <span className="text-sm text-verba-indigo dark:text-verba-indigo-dk font-medium">
                      {existingFilesCountMessage}
                    </span>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  innerText="Cancel"
                  onClick={() => {
                    setFile([]);
                    setFileProperties(new Map());
                    setFileCampaignBranches(new Map());
                    setExistingFiles(new Set());
                  }}
                  disabled={isLoading}
                  buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                  innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
                />
                <Button
                  type="button"
                  onClick={() => handleBulkUpload(file)}
                  disabled={
                    isLoading ||
                    file.length === 0 ||
                    file.filter((f) => !existingFiles.has(f.name)).length === 0
                  }
                  isLoading={isLoading}
                  innerText={`Confirm Upload (${
                    file.filter((f) => !existingFiles.has(f.name)).length
                  })`}
                  buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed hover:bg-verba-blue dark:hover:bg-verba-blue-dk text-pure-white dark:text-pure-white-dk hover:text-pure-white dark:hover:text-pure-white"
                  innerTextClass="text-md font-medium whitespace-nowrap"
                />
              </div>
            </div>
          )}
          {(success || error) && (
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                innerText="Close"
                onClick={() => {
                  setFile([]);
                  setFileProperties(new Map());
                  setExistingFiles(new Set());
                  setFileCampaignBranches(new Map());
                  setSuccess(false);
                  setIsLoading(false);
                  setError(false);
                  onClose();
                }}
                disabled={isLoading}
                buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed hover:bg-verba-blue dark:hover:bg-verba-blue-dk text-pure-white dark:text-pure-white-dk hover:text-pure-white dark:hover:text-pure-white"
                innerTextClass="text-md font-medium whitespace-nowrap"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UploadDocuments;
